import type { FunctionComponent } from 'react';
import { useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { useAutoFocusRef } from '@trello/dom-hooks';
import { sendErrorEvent } from '@trello/error-reporting';
import type { NetworkError } from '@trello/graphql-error-handling';
import { isSubmitEvent } from '@trello/keybindings';
import { Button } from '@trello/nachos/button';
import { Textfield } from '@trello/nachos/textfield';
import type { TeamTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useAddTagMutation } from './AddTagMutation.generated';
import { TagsDocument, useTagsQuery } from './TagsQuery.generated';

import styles from './CreateNewCollection.less';

interface CreateNewCollectionProps {
  orgId: string;
  onCreate: (tagName: string) => void;
}

export const CreateNewCollection: FunctionComponent<
  CreateNewCollectionProps
> = ({ orgId, onCreate }) => {
  const existingTagsQuery = useTagsQuery({
    variables: { orgId },
    waitOn: ['MemberHeader', 'MemberBoards', 'WorkspaceBoardsPageMinimal'],
  });
  const [addTag, { error }] = useAddTagMutation();
  const [savingTag, setSavingTag] = useState(false);
  const [tagIsDuplicate, setTagIsDuplicate] = useState(false);

  const [tagName, setTagName] = useState<string>('');

  const inputElement = useRef<HTMLInputElement>(null);

  const saveTag = useCallback(
    async (name?: string) => {
      const existingTags = existingTagsQuery.data;

      if (!existingTags) {
        return;
      }

      const nameTrimmed = name?.trim() || '';
      if (!nameTrimmed) {
        return;
      }

      if (
        existingTags.organization?.tags?.some((tag) => tag.name === nameTrimmed)
      ) {
        setTagIsDuplicate(true);
        return;
      }

      setSavingTag(true);
      const source = 'workspaceBoardsScreen';
      const traceId = Analytics.startTask({
        taskName: 'create-tag',
        source,
      });
      try {
        await addTag({
          variables: {
            orgId,
            tag: nameTrimmed,
          },
          refetchQueries: [
            {
              query: TagsDocument,
              variables: { orgId },
              context: {
                operationName: 'AddTagMutation',
                document: TagsDocument,
              },
            },
          ],
        });
        Analytics.sendTrackEvent({
          action: 'created',
          actionSubject: 'collection',
          source,
          attributes: {
            taskId: traceId,
            isBCFeature: true,
            requiredBC: true,
          },
          containers: {
            workspace: { id: orgId },
          },
        });
        Analytics.taskSucceeded({
          taskName: 'create-tag',
          traceId,
          source,
        });
        setSavingTag(false);
        onCreate(nameTrimmed);
      } catch (err) {
        const tracedError = Analytics.taskFailed({
          taskName: 'create-tag',
          traceId,
          source,
          error: err,
        });
        sendErrorEvent(tracedError || err, {
          tags: {
            ownershipArea: 'trello-web-eng',
            feature: 'Collections',
          },
        });
        setSavingTag(false);
      }
    },
    [orgId, addTag, onCreate, existingTagsQuery.data],
  );

  useAutoFocusRef(inputElement, 1);

  const onKeyDown = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      if (isSubmitEvent(e)) {
        saveTag(tagName);
      }
    },
    [saveTag, tagName],
  );

  return (
    <div>
      <Textfield
        label={
          <FormattedMessage
            id="templates.workspace_page.name"
            defaultMessage="Name"
            description="The name of the tag"
          />
        }
        value={tagName}
        ref={inputElement}
        onChange={(e) => {
          setTagName(e.target.value);
          setTagIsDuplicate(false);
        }}
        onKeyDown={onKeyDown}
        data-testid={getTestId<TeamTestIds>('collection-name-input')}
      />
      <Button
        appearance="primary"
        className={styles.saveButton}
        isDisabled={!tagName.trim() || existingTagsQuery.loading}
        onClick={() => saveTag(tagName)}
        isLoading={savingTag}
        data-testid={getTestId<TeamTestIds>('collection-name-save-button')}
      >
        <FormattedMessage
          id="templates.workspace_page.save"
          defaultMessage="Save"
          description="The text of the button to save the tag"
        />
      </Button>
      {error ? (
        <p>
          {(error.networkError as unknown as NetworkError | undefined)
            ?.status === 409 ? (
            <FormattedMessage
              id="templates.workspace_page.collection-with-that-name-already-exists"
              defaultMessage="Error saving the collection: a collection with that name already exists."
              description="An error message displayed when a collection with the same name already exists"
            />
          ) : (
            <FormattedMessage
              id="templates.workspace_page.error-saving-collection"
              defaultMessage="There was an error saving the collection."
              description="An error message displayed when there was an error saving the collection"
            />
          )}
        </p>
      ) : (
        tagIsDuplicate && (
          <p>
            <FormattedMessage
              id="templates.workspace_page.collection-with-that-name-already-exists"
              defaultMessage="Error saving the collection: a collection with that name already exists."
              description="An error message displayed when a collection with the same name already exists"
            />
          </p>
        )
      )}
    </div>
  );
};
